/**
 * The tabs of Storefront → Payments, in the strip's order. The tab rides the URL (`?tab=`),
 * so a tab is a link: the legacy packs and currency bookmarks land on theirs, and the Stripe
 * onboarding return leg lands on the account.
 */
export const PAYMENTS_TABS = ["account", "currency", "packs", "purchases", "disputes"] as const;
export type PaymentsTab = (typeof PAYMENTS_TABS)[number];

export const PAYMENTS_TAB_LABELS: Record<PaymentsTab, string> = {
  account: "Stripe account",
  currency: "Currency",
  packs: "Packs",
  purchases: "Purchases",
  disputes: "Disputes",
};

/** The tabs an admin alone sees; their queries never run below that role. */
const ADMIN_TABS: ReadonlySet<PaymentsTab> = new Set<PaymentsTab>([
  "account",
  "purchases",
  "disputes",
]);

export function isPaymentsTab(value: unknown): value is PaymentsTab {
  return typeof value === "string" && (PAYMENTS_TABS as readonly string[]).includes(value);
}

export interface PaymentsSearch {
  tab?: PaymentsTab;
}

/** The route's `validateSearch`: an unknown tab is dropped, and the page shows its first. */
export function parsePaymentsSearch(search: Record<string, unknown>): PaymentsSearch {
  return { tab: isPaymentsTab(search.tab) ? search.tab : undefined };
}

/** The tabs a member may see, in the strip's order. */
export function visiblePaymentsTabs(isAdmin: boolean): readonly PaymentsTab[] {
  return isAdmin ? PAYMENTS_TABS : PAYMENTS_TABS.filter((tab) => !ADMIN_TABS.has(tab));
}

/**
 * The tab to show: the requested one when the member may see it, else the first they may, so
 * a viewer following an admin's link to the disputes sees the currency and never an empty
 * panel.
 */
export function resolvePaymentsTab(
  requested: PaymentsTab | undefined,
  isAdmin: boolean,
): PaymentsTab {
  const tabs = visiblePaymentsTabs(isAdmin);
  if (requested && tabs.includes(requested)) return requested;
  // The currency is every member's, so the list is never empty.
  return tabs[0] ?? "currency";
}
